import uuid from 'uuid/v4';
import {
  AssignmentCreationWithoutSubjectInput,
  AssignmentUpdateWithoutSubjectInput,
  GetRawArticleMajorVersionsQuery_query_rawArticle_RawArticle_majorVersions_RawArticleVersion,
  GetSubjectQuery,
  MediaSearchStatus,
  RawArticleStatus,
  SubjectCreationInput,
  SubjectForm_FlatAssignmentFragment_FlatAssignment_printHeading_PrintHeading,
  UpsertSubjectMutationVariables,
} from '../../../__generated__/queries-topic';

export interface AssignmentFormModel {
  id: string | null;
  supportId?: string | null;
  supportTitle: string;
  supportKey?: string;
  printPublicationId?: string;
  printIssueId: string;
  printIssueTitle: string;
  printHeadingId: string;
  printTemplateId: string;
  websiteId: string;
  autoPrototype: boolean;
  printHeadingTitle: string;
  websiteTitle: string;
  mediaSearchStatus: MediaSearchStatus;
  rawArticle?: {
    id: string;
    versions?: {
      id: string;
      status: RawArticleStatus;
      title?: string | null;
      editor?:
        | {
            name: string;
            id: string;
          }
        | null
        | undefined;
      createdAt?: string | undefined;
    }[];
  } | null;
  subject: {
    id: string;
  };
  needPortal?: boolean;
  isCreate?: boolean;
  versionSelected?: GetRawArticleMajorVersionsQuery_query_rawArticle_RawArticle_majorVersions_RawArticleVersion;
  articleTemplate: {
    id: string;
  } | null;
  printHeading: SubjectForm_FlatAssignmentFragment_FlatAssignment_printHeading_PrintHeading | null;
}

enum Locations {
  PrintPublication = 'printPublication',
  PrintIssue = 'printIssue',
  PrintHeading = 'printHeading',
  Website = 'website',
}

export const getEmptyAssignment = (subjectId: string): AssignmentFormModel => ({
  id: null,
  supportId: null,
  supportTitle: '',
  printIssueId: '',
  printHeadingId: '',
  printTemplateId: '',
  websiteId: '',
  autoPrototype: false,
  printHeadingTitle: '',
  printIssueTitle: '',
  printPublicationId: '',
  websiteTitle: '',
  mediaSearchStatus: MediaSearchStatus.Waiting,
  rawArticle: {
    id: '',
    versions: [],
  },
  subject: {
    id: subjectId,
  },
  articleTemplate: null,
  printHeading: null,
});
export interface SubjectFormModel {
  id: string;
  title: string;
  synopsis: string;
  mediaRequest: string;
  comment: string;
  creatorName: string;
  subjectCategory: string;
  subSubjectCategory: string;
  assignments: AssignmentFormModel[];
  unit: string;
  isCreate?: boolean;
  isClassified: boolean | null;
}

export const apiToForm = (
  queryData: GetSubjectQuery | { subject: null },
): SubjectFormModel => {
  if (!queryData.subject) {
    return {
      creatorName: '',
      id: '',
      title: '',
      synopsis: '',
      comment: '',
      subjectCategory: '',
      subSubjectCategory: '',
      mediaRequest: '',
      assignments: [],
      unit: '',
      isClassified: false,
    };
  }
  const { subject } = queryData;

  return {
    id: subject.id,
    title: subject.title,
    synopsis: subject.synopsis || '',
    mediaRequest: subject.mediaRequest || '',
    comment: subject.comment || '',
    creatorName: subject.createdBy ? subject.createdBy.name : '',
    // eslint-disable-next-line fp/no-mutating-methods
    assignments: [...subject.assignments]
      .sort((a, b) => {
        return (
          new Date(a.assignment.createdAt).getTime() -
          new Date(b.assignment.createdAt).getTime()
        );
      })
      .map((assignment) => ({
        mediaSearchStatus:
          (assignment.assignment && assignment.assignment.mediaSearchStatus) ||
          MediaSearchStatus.Waiting,
        id: (assignment.assignment && assignment.assignment.id) || '',
        supportId:
          (assignment.printPublication &&
            `print:${assignment.printPublication.id}`) ||
          (assignment.website &&
            `web:${assignment.website.id}:${assignment.website.key}`),
        supportTitle:
          (assignment.printPublication && assignment.printPublication.title) ||
          '',
        supportKey: assignment.website ? assignment.website.key : undefined,
        key: assignment.website ? assignment.website.key : undefined,
        websiteTitle: assignment.website ? assignment.website.title : '',
        printIssueId: (assignment.printIssue && assignment.printIssue.id) || '',
        printPublicationId:
          (assignment.printPublication && assignment.printPublication.id) || '',
        websiteId: (assignment.website && assignment.website.id) || '',
        printHeadingId:
          (assignment.printHeading && assignment.printHeading.id) || '',
        printTemplateId: assignment.assignment?.printTemplate?.id || '',
        autoPrototype:
          Boolean(assignment.printHeading?.autoPrototype) ||
          Boolean(assignment.assignment?.printTemplate?.id),
        printHeadingTitle:
          (assignment.printHeading && assignment.printHeading.title) || '',
        printIssueTitle:
          (assignment.printIssue && assignment.printIssue.title) || '',
        rawArticle: assignment.rawArticle || null,
        subject: { id: subject.id },
        articleTemplate: assignment.assignment?.articleTemplate || null,
        printHeading: assignment.printHeading || null,
      })),
    subjectCategory: subject.subjectCategory
      ? subject.subjectCategory.parent
        ? subject.subjectCategory.parent.id
        : subject.subjectCategory.id
      : '',
    subSubjectCategory:
      subject.subjectCategory && subject.subjectCategory.parent
        ? subject.subjectCategory.id
        : '',
    unit: subject.unit.id,
    isClassified: subject.isClassified,
  };
};

const getCreateAssignmentFromSubject = (): AssignmentCreationWithoutSubjectInput => ({
  printPublication: null,
  printIssue: null,
  printHeading: null,
  rawArticle: null,
  website: null,
});

export const getAssignmentConnections = (
  assignment: AssignmentFormModel,
  assignmentConnections:
    | AssignmentCreationWithoutSubjectInput
    | AssignmentUpdateWithoutSubjectInput,
  fromUpdate: boolean,
): AssignmentCreationWithoutSubjectInput => {
  if (
    assignment.supportId &&
    !assignment.printIssueId &&
    !assignment.printHeadingId
  ) {
    const [type, id] = assignment.supportId.split(':');
    if (type === 'print') {
      // eslint-disable-next-line immutable/no-mutation
      assignmentConnections.printPublication = {
        connect: { id },
      };
    } else if (type === 'web') {
      // eslint-disable-next-line immutable/no-mutation
      assignmentConnections.website = {
        connect: { id },
      };
    }
  }
  if (assignment.printIssueId && !assignment.printHeadingId) {
    // eslint-disable-next-line immutable/no-mutation
    assignmentConnections.printIssue = {
      connect: { id: assignment.printIssueId },
    };
  }
  if (assignment.printHeadingId) {
    // eslint-disable-next-line immutable/no-mutation
    assignmentConnections.printHeading = {
      connect: { id: assignment.printHeadingId },
    };
  }
  if (assignment.rawArticle && assignment.rawArticle.id) {
    // eslint-disable-next-line immutable/no-mutation
    assignmentConnections.rawArticle = {
      connect: { id: assignment.rawArticle.id },
    };
  }

  if (assignment.id && !fromUpdate) {
    // eslint-disable-next-line immutable/no-mutation
    (assignmentConnections as AssignmentCreationWithoutSubjectInput).id =
      assignment.id;
  }

  if (fromUpdate) {
    // disconnect other location relations
    Object.values(Locations).forEach((location) => {
      if (!assignmentConnections[location]) {
        switch (location) {
          case Locations.PrintHeading:
            if (assignment.printHeadingId)
              // eslint-disable-next-line immutable/no-mutation
              assignmentConnections[location] = { disconnectIfExists: true };
            break;
          case Locations.PrintIssue:
            if (assignment.printIssueId)
              // eslint-disable-next-line immutable/no-mutation
              assignmentConnections[location] = { disconnectIfExists: true };
            break;
          case Locations.PrintPublication:
            if (assignment.printPublicationId)
              // eslint-disable-next-line immutable/no-mutation
              assignmentConnections[location] = { disconnectIfExists: true };
            break;
          case Locations.Website:
            if (assignment.websiteId)
              // eslint-disable-next-line immutable/no-mutation
              assignmentConnections[location] = { disconnectIfExists: true };
            break;
        }
      }
    });
  }
  return assignmentConnections;
};

export const formToApi = (
  formData: SubjectFormModel,
): UpsertSubjectMutationVariables => {
  const model: SubjectCreationInput = {
    title: formData.title,
    synopsis: formData.synopsis,
    comment: formData.comment || null,
    mediaRequest: formData.mediaRequest,
    unit: { connect: { id: formData.unit } },
    assignments: {
      create: formData.assignments
        .filter((assignment) => assignment.isCreate || !assignment.id)
        .map((assignment) => {
          const newRawArticleId = uuid();
          const newRawArticle = !assignment.versionSelected
            ? {}
            : {
                rawArticle: {
                  create: {
                    id: newRawArticleId,
                    versions: {
                      create: [
                        {
                          id: uuid(),
                          editorId: assignment.versionSelected.editorId,
                          title: assignment.versionSelected.title,
                          lead: assignment.versionSelected.lead,
                          body: assignment.versionSelected.body,
                          signature: assignment.versionSelected.signature,
                          status: assignment.versionSelected.status,
                        },
                      ],
                    },
                  },
                },
              };

          return {
            mediaSearchStatus: assignment.mediaSearchStatus,
            ...getAssignmentConnections(
              assignment,
              getCreateAssignmentFromSubject(),
              false,
            ),
            ...newRawArticle,
          };
        }),
    },
    isClassified: formData.isClassified,
  };

  const subjectCategoryId =
    formData.subSubjectCategory || formData.subjectCategory;

  if (subjectCategoryId) {
    // eslint-disable-next-line immutable/no-mutation
    model.subjectCategory = {
      connect: {
        id: subjectCategoryId,
      },
    };
  }

  const id = formData.id ? formData.id : uuid();

  return {
    create: { id, ...model },
    update: { ...model },
    where: { id },
  };
};
